import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, IconButton, MenuItem, Paper, Snackbar,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import UndoIcon from '@mui/icons-material/Undo';
import SendIcon from '@mui/icons-material/Send';
import { getCurrentUser } from '../auth/LoginButton';
import { getUserRoles } from '../rbac/roles';
import {
  getDripLeads, stopDripLead, dripErrorMessage, verityHoldText, VERITY_SYSTEM_ACTOR, type DripLead,
  markWelcomedOutside, clearWelcomedOutside, previewSendNow, sendNow, type SendNowPreview,
} from '../services/drip-api';
// Server times are naive UTC strings: read as UTC, shown in local time; a day cell shows the Pacific day.
import { formatServerTime as when, pacificDay } from '../services/server-time';

/**
 * Every lead SIMS holds — the same people the EAB importer sent to Verity.
 *
 * This exists because the drip was otherwise a black box: entries could be written and a sequence
 * could run, with no way to see who was actually in it. A person deciding whether to stop someone
 * needs to see them first.
 *
 * Campaign is a first-class column and filter even though EAB is currently the only one. The field
 * is already on every lead, the table is built to group by it, and adding a second source later is
 * then a data change rather than a screen change.
 */

type SortKey = 'arrivedAt' | 'email' | 'firstName' | 'source' | 'status' | 'sent';

// Layout tiers (standing rule): the base is the laptop; the media queries below never reach it.
const WIDE = '@media (min-width: 1800px)';
// The 34-inch ultrawide: the 40px inset of the Drip Campaign sub-tab bar above, so switching sub-tabs
// never moves the left edge.
const ULTRAWIDE = '@media (min-width: 2400px)';

const STATUS_STYLE: Record<string, { label: string; color: string; text?: string; border?: string }> = {
  active: { label: 'In sequence', color: 'var(--accent)' },
  held: { label: 'On hold', color: 'var(--warning)', text: '#000' },
  stopped: { label: 'Stopped', color: 'var(--error)' },
  completed: { label: 'Finished', color: 'var(--success)' },
  not_started: { label: 'Not started', color: 'transparent', text: 'var(--text-primary)', border: '1px solid var(--card-border)' },
  merged: { label: 'Merged', color: 'transparent', text: 'var(--text-secondary)', border: '1px dashed var(--card-border)' },
};

/** A status this build does not know is shown as itself, outlined, never thrown on. */
function statusChipSx(status: string) {
  const st = STATUS_STYLE[status];
  return st
    ? { fontSize: '0.7rem', backgroundColor: st.color, color: st.text || '#fff', border: st.border }
    : { fontSize: '0.7rem', backgroundColor: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--card-border)' };
}

// Mark and Undo are for the roles every lead action needs; the server enforces them either way.
const WRITE_ROLES = ['Survey.Admin', 'Drip.Manager'];

/**
 * Welcomed outside SIMS: Admissions sent this lead its first email by hand (Outlook), so SIMS skips
 * its own welcome and starts at the next email. A mark can be set, or its date changed, only on a lead
 * SIMS may still email for the first time, and undone only until SIMS has emailed it - the server says
 * why not.
 */
function canMarkWelcomed(l: DripLead): boolean {
  return (l.status === 'active' || l.status === 'held') && !(l.sentSteps || []).length;
}

function canUndoWelcomed(l: DripLead): boolean {
  return !!l.welcomedOutside && !(l.sentSteps || []).length && l.status !== 'merged';
}

/**
 * The day a lead arrived (utils/drip_engine.welcome_arrival_date): the Pacific day of its arrival time,
 * except that an uploaded file's date is stored as naive midnight ('2026-10-05T00:00:00') and is that
 * calendar day, not the Pacific evening before. The Arrived cells and the welcome dialog both show it.
 */
function arrivalDate(iso?: string | null): string {
  const naive = /^(\d{4}-\d{2}-\d{2})T00:00:00$/.exec((iso || '').trim());
  return naive ? naive[1] : pacificDay(iso);
}

function welcomedText(l: DripLead): string {
  const w = l.welcomedOutside;
  if (!w) return '';
  return `Admissions sent the first email by hand${w.date ? ` on ${w.date}` : ''}. SIMS skips its welcome and `
    + 'starts at the next email.' + (w.by ? ` Marked by ${w.by}${w.markedAt ? ` on ${when(w.markedAt)}` : ''}.` : '');
}

/**
 * Send email now: a person sends one lead its next email from here at once, instead of Outlook. Since
 * 2026-10-27 the automatic runs use the address on SIMS's record when Verity lists none, so this is no
 * longer the only sender - but in a dry run it is the only way anything is sent. Offered on any lead
 * SIMS may still email; the server's preview says which email is next, or why it cannot go now.
 */
function canSendNow(l: DripLead): boolean {
  return l.status === 'active' || l.status === 'held';
}

function lastSentText(l: DripLead): string {
  if (!l.lastSentStep) return 'none';
  const history = l.sentHistory || [];
  const last = history[history.length - 1];
  const byHand = last?.manual ? ` — sent by hand with Send email now${last.by ? ` by ${last.by}` : ''}` : '';
  return `${l.lastSentStep} (${l.lastSentDate})${byHand}`;
}

/** "A", "A or B", "A, B or C". */
function orList(items: string[]): string {
  return items.length <= 1 ? (items[0] || '') : `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;
}

/** The reason as the server keeps it: runs of whitespace become one space (utils/drip_verity.clean_reason). */
function cleanReason(text: string): string {
  return text.split(/\s+/).filter(Boolean).join(' ');
}

/**
 * What the Verity status check last said about this lead. Kinds and times only: a status name
 * appears only inside a stop reason, which goes in the tooltip. An address-not-usable hold says only
 * that: why is counted on the run (Last runs on Drip Emails), never kept on a lead.
 */
function verityChip(l: DripLead): { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info'; at?: string | null; detail?: string } {
  if (l.status === 'stopped' && l.stoppedBy === VERITY_SYSTEM_ACTOR) {
    return { label: 'Stopped by Verity', color: 'error', at: l.stoppedAt, detail: l.stoppedReason };
  }
  if (l.status === 'held') {
    return { label: `Held: ${verityHoldText(l.verityHold?.kind).short}`, color: 'warning', at: l.verityHold?.since };
  }
  const shadow = l.verityShadow;
  if (shadow?.action === 'stop') return { label: 'Would stop (dry run)', color: 'warning', at: shadow.since };
  if (shadow?.action === 'hold') {
    return { label: `Would hold: ${verityHoldText(shadow.kind).short} (dry run)`, color: 'info', at: shadow.since };
  }
  if (shadow?.action === 'allow') return { label: 'Would allow (dry run)', color: 'info', at: shadow.since };
  const check = l.verityCheck;
  if (check?.action === 'allow') return { label: 'Allowed', color: 'success', at: check.since };
  if (check?.action === 'stop') return { label: 'Verity said stop', color: 'default', at: check.since };
  if (check?.action === 'hold') return { label: `Was held: ${verityHoldText(check.kind).short}`, color: 'default', at: check.since };
  if (l.status === 'active') return { label: 'Not checked yet', color: 'default' };
  return { label: '—', color: 'default' };
}

export default function VerityLeads() {
  const [leads, setLeads] = useState<DripLead[]>([]);
  const [activeEntries, setActiveEntries] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [campaign, setCampaign] = useState('all');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('arrivedAt');
  const [sortDesc, setSortDesc] = useState(true);
  const [stopping, setStopping] = useState<string | null>(null);
  const [detail, setDetail] = useState<DripLead | null>(null);
  const [takeOver, setTakeOver] = useState<DripLead | null>(null);
  const [welcomeMark, setWelcomeMark] = useState<DripLead | null>(null);
  const [welcomeUndo, setWelcomeUndo] = useState<DripLead | null>(null);
  const [welcomeDate, setWelcomeDate] = useState('');
  const [welcomeBusy, setWelcomeBusy] = useState(false);
  const [welcomeError, setWelcomeError] = useState<string | null>(null);
  const [sendLead, setSendLead] = useState<DripLead | null>(null);
  const [sendPreview, setSendPreview] = useState<SendNowPreview | null>(null);
  const [sendAttest, setSendAttest] = useState(false);
  const [sendReason, setSendReason] = useState('');
  const [sendBusy, setSendBusy] = useState<'checking' | 'sending' | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  // The lead the Send email now dialog is for: an answer that arrives after it closed, or after it
  // was reopened for another lead, is dropped.
  const sendFor = useRef<string | null>(null);
  const canWrite = getUserRoles(getCurrentUser()).some((r) => WRITE_ROLES.includes(r));

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getDripLeads();
      setLeads(res.leads || []);
      setActiveEntries(res.activeEntries || []);
    } catch (e) {
      setError(dripErrorMessage(e, 'The leads could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const campaigns = useMemo(
    () => Array.from(new Set(leads.map((l) => l.source).filter(Boolean))).sort(),
    [leads]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = leads.filter((l) =>
      (campaign === 'all' || l.source === campaign) &&
      (status === 'all' || l.status === status) &&
      (!q || [l.email, l.firstName, l.lastName, l.programInterest, l.city]
        .some((v) => (v || '').toLowerCase().includes(q))));

    const val = (l: DripLead): string | number => {
      switch (sortKey) {
        case 'sent': return (l.sentSteps || []).length;
        case 'email': return (l.email || '').toLowerCase();
        case 'firstName': return `${l.firstName || ''} ${l.lastName || ''}`.trim().toLowerCase();
        case 'source': return l.source || '';
        case 'status': return l.status || '';
        default: return l.arrivedAt || '';
      }
    };
    return [...rows].sort((a, b) => {
      const x = val(a), y = val(b);
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
      return sortDesc ? -c : c;
    });
  }, [leads, campaign, status, search, sortKey, sortDesc]);

  const stats = useMemo(() => ({
    total: leads.length,
    active: leads.filter((l) => l.status === 'active').length,
    held: leads.filter((l) => l.status === 'held').length,
    stopped: leads.filter((l) => l.status === 'stopped').length,
    completed: leads.filter((l) => l.status === 'completed').length,
    notStarted: leads.filter((l) => l.status === 'not_started').length,
    merged: leads.filter((l) => l.status === 'merged').length,
    fromImporter: leads.filter((l) => (l.importedFrom || '').startsWith('eab')).length,
  }), [leads]);

  const sortOn = (key: SortKey) => {
    if (key === sortKey) setSortDesc(!sortDesc);
    else { setSortKey(key); setSortDesc(true); }
  };

  // A lead the Verity check stopped can be resumed by a person. Stopping it here takes the stop
  // over and makes it final (Resume and Undo then refuse it), so that one case asks first.
  const stop = async (lead: DripLead) => {
    setStopping(lead.id);
    const final = lead.stoppedBy === VERITY_SYSTEM_ACTOR;
    try {
      const { lead: now } = await stopDripLead(lead.id, final
        ? 'Verity stop made final from the Verity Leads dashboard'
        : 'Stopped from the Verity Leads dashboard');
      // The server answers with the record as it now stands (for a merged row, the record it was
      // merged into). Only a stopped, completed or not-started record receives nothing further.
      const finished = ['stopped', 'completed', 'not_started'].includes(now?.status ?? '');
      setToast(!finished
        ? `${lead.firstName || lead.email} could not be stopped - the list was out of date. It has been refreshed; check the lead and try again.`
        : final
          ? `${lead.firstName || lead.email}: the stop is final now.`
          : `${lead.firstName || lead.email} will receive nothing further.`);
      await load();
    } catch (e) {
      setToast(dripErrorMessage(e, 'That lead could not be stopped.'));
    } finally {
      setStopping(null);
    }
  };

  // A marked lead opens with its current date, to change it (a weekend lead welcomed on Monday, say).
  const openWelcome = (lead: DripLead, undo: boolean) => {
    setWelcomeDate(undo ? '' : lead.welcomedOutside?.date || ''); setWelcomeError(null); setWelcomeBusy(false);
    if (undo) setWelcomeUndo(lead); else setWelcomeMark(lead);
  };

  // Mark or undo "Welcomed outside SIMS". A refusal (bad date, already emailed by SIMS, changed under
  // us) is shown in the dialog in the server's own words; the dialog stays open so it can be fixed.
  const saveWelcome = async () => {
    const lead = welcomeMark || welcomeUndo;
    if (!lead) return;
    setWelcomeBusy(true); setWelcomeError(null);
    try {
      if (welcomeMark) {
        await markWelcomedOutside(lead.id, welcomeDate.trim() || undefined);
        setToast(`${lead.firstName || lead.email}: ${lead.welcomedOutside ? 'welcome date changed' : 'welcomed outside SIMS'}.`
          + ' SIMS starts at the next email.');
      } else {
        await clearWelcomedOutside(lead.id);
        setToast(`${lead.firstName || lead.email}: mark undone. SIMS sends its own welcome first.`);
      }
      setWelcomeMark(null); setWelcomeUndo(null);
      await load();
    } catch (e) {
      setWelcomeError(dripErrorMessage(e, 'That could not be saved.'));
    } finally {
      setWelcomeBusy(false);
    }
  };

  // Send email now. The preview is the server's answer to "which email, and can it go now?", from
  // its own Verity check; nothing is sent until the person confirms what they checked in Verity, gives
  // a reason and presses Send. After a failed send the send's own error stays: it says whether the
  // email still counts as sent, so a preview that fails as well must not replace it.
  const refreshSendPreview = async (lead: DripLead, keepError = false) => {
    try {
      const preview = await previewSendNow(lead.id);
      if (sendFor.current === lead.id) setSendPreview(preview);
    } catch (e) {
      if (sendFor.current === lead.id) {
        setSendPreview(null);
        if (!keepError) setSendError(dripErrorMessage(e, 'SIMS could not check whether this lead can be emailed.'));
      }
    }
  };

  const openSendNow = async (lead: DripLead) => {
    sendFor.current = lead.id;
    setSendLead(lead); setSendPreview(null); setSendAttest(false); setSendReason(''); setSendError(null);
    setSendBusy('checking');
    await refreshSendPreview(lead);
    if (sendFor.current === lead.id) setSendBusy(null);
  };

  const closeSendNow = () => {
    sendFor.current = null;
    setSendLead(null); setSendPreview(null); setSendBusy(null);
  };

  // A refusal or a SendGrid failure stays in the dialog in the server's own words (it says whether
  // the email still counts as sent), and the preview and the list are read again so both show the
  // lead as it now stands.
  const doSendNow = async () => {
    const lead = sendLead;
    const entry = sendPreview?.entry;
    const address = sendPreview?.address;
    if (!lead || !entry || !address) return;
    setSendBusy('sending'); setSendError(null);
    try {
      const res = await sendNow(lead.id, entry.step, address, cleanReason(sendReason));
      setToast(`${lead.firstName || lead.email}: “${res.entry?.name || entry.name}” sent.`);
      closeSendNow();
      await load();
      return;
    } catch (e) {
      if (sendFor.current !== lead.id) return;
      setSendError(dripErrorMessage(e, 'The email could not be sent.'));
      setSendAttest(false);
      await refreshSendPreview(lead, true);
      void load();
    }
    if (sendFor.current === lead.id) setSendBusy(null);
  };

  const head = (key: SortKey, label: string) => (
    <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
      <TableSortLabel active={sortKey === key} direction={sortDesc ? 'desc' : 'asc'}
                      onClick={() => sortOn(key)}>{label}</TableSortLabel>
    </TableCell>
  );

  return (
    <Box sx={{ p: 3, [ULTRAWIDE]: { px: 5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ color: 'var(--text-primary)' }}>Verity Leads</Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 780 }}>
            Every lead SIMS holds — the same record the EAB importer sent to Verity. Leads arrive
            automatically when EAB deposits a file, or from a file uploaded on Drip Emails (Drip
            Campaign tab). Click a row for the full record.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload">
          <span>
            <IconButton onClick={() => void load()} disabled={loading}
                        sx={{ color: 'var(--text-secondary)' }}><RefreshIcon /></IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Nothing sends while no entry is active. Said here rather than left to be discovered,
          because a screen full of leads reads as a running campaign. */}
      {!loading && !error && activeEntries.length === 0 && leads.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          These leads are registered but <b>no email is being sent</b> — no drip entry is active yet.
          Activate the cadence on Drip Emails when you are ready.
        </Alert>
      )}

      {/* Laptop: two rows of four. From 1800px: one row of eight. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 2, mb: 3,
                 [WIDE]: { gridTemplateColumns: 'repeat(8, minmax(0, 1fr))' } }}>
        {([['Total', stats.total, 'var(--text-primary)'],
           ['In sequence', stats.active, 'var(--accent)'],
           ['On hold', stats.held, 'var(--warning)'],
           ['Stopped', stats.stopped, 'var(--error)'],
           ['Finished', stats.completed, 'var(--success)'],
           ['Not started', stats.notStarted, 'var(--text-primary)'],
           ['Merged', stats.merged, 'var(--text-secondary)'],
           ['From EAB automatically', stats.fromImporter, 'var(--text-primary)']] as const)
          .map(([label, value, color]) => (
            <Paper key={label} sx={{ p: 2, textAlign: 'center', minWidth: 0 }}>
              <Typography variant="h4" sx={{ color }}>{value}</Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>{label}</Typography>
            </Paper>
          ))}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <TextField select size="small" label="Campaign" value={campaign}
                     onChange={(e) => setCampaign(e.target.value)}>
            <MenuItem value="all">All campaigns</MenuItem>
            {campaigns.map((c) => (
              <MenuItem key={c} value={c}>{c.toUpperCase()}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Status" value={status}
                     onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">In sequence</MenuItem>
            <MenuItem value="held">On hold</MenuItem>
            <MenuItem value="stopped">Stopped</MenuItem>
            <MenuItem value="completed">Finished</MenuItem>
            <MenuItem value="not_started">Not started</MenuItem>
            <MenuItem value="merged">Merged</MenuItem>
          </TextField>
          <TextField size="small" label="Search" placeholder="Name, email, programme, city…"
                     value={search} onChange={(e) => setSearch(e.target.value)} />
        </Box>
      </Paper>

      {loading && <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && (
        shown.length === 0 ? (
          <Alert severity="info">
            {leads.length === 0
              ? 'No leads yet. They arrive automatically when EAB deposits a file, or you can upload one on Drip Emails.'
              : 'No leads match these filters.'}
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {head('firstName', 'Name')}
                  {head('email', 'Email')}
                  {head('source', 'Campaign')}
                  <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>Programme</TableCell>
                  <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>Location</TableCell>
                  {head('arrivedAt', 'Arrived')}
                  {head('status', 'Status')}
                  {head('sent', 'Emails')}
                  <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>Next</TableCell>
                  <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>Verity</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((l) => (
                  <TableRow key={l.id} hover sx={{ cursor: 'pointer' }}
                            onClick={() => setDetail(l)}>
                    <TableCell sx={{ color: 'var(--text-primary)' }}>
                      {[l.firstName, l.lastName].filter(Boolean).join(' ') || '—'}
                    </TableCell>
                    <TableCell sx={{ color: 'var(--text-secondary)' }}>{l.email}</TableCell>
                    <TableCell>
                      <Chip size="small" label={(l.source || '—').toUpperCase()}
                            sx={{ fontSize: '0.7rem' }} />
                      {/* Which path the lead came in by. A person chasing a missing lead needs to
                          know whether to look at the importer or at someone's upload. */}
                      <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)' }}>
                        {(l.importedFrom || 'manual').startsWith('eab') ? 'automatic' : 'uploaded'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: 'var(--text-secondary)' }}>{l.programInterest || '—'}</TableCell>
                    <TableCell sx={{ color: 'var(--text-secondary)' }}>
                      {[l.city, l.state].filter(Boolean).join(', ') || '—'}
                    </TableCell>
                    {/* The Pacific day, not the UTC date: an evening arrival is that day, not the next. */}
                    <TableCell sx={{ color: 'var(--text-secondary)' }}>
                      {arrivalDate(l.arrivedAt) || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={STATUS_STYLE[l.status]?.label || l.status || 'unknown'}
                            sx={statusChipSx(l.status)} />
                      {l.welcomedOutside && (
                        <Tooltip title={welcomedText(l)}>
                          {/* Wraps like the Verity chip, so the badge cannot widen the table. */}
                          <Chip size="small" variant="outlined" color="info" label="Welcomed outside SIMS"
                                sx={{ display: 'flex', mt: 0.5, width: 'fit-content', fontSize: '0.7rem', height: 'auto',
                                      maxWidth: 130, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: 'var(--text-secondary)', textAlign: 'center' }}>
                      {(l.sentSteps || []).length}
                    </TableCell>
                    <TableCell sx={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                      {l.nextStep || l.nextStepReason || '—'}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const v = verityChip(l);
                        if (v.label === '—') return <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>—</Typography>;
                        const tip = [v.at ? `Since ${when(v.at)}` : '', v.detail || ''].filter(Boolean).join(' — ');
                        // The label wraps: a one-line chip widened the table past a laptop screen and
                        // pushed the Stop button out of view.
                        const chip = <Chip size="small" variant="outlined" label={v.label} color={v.color}
                                           sx={{ fontSize: '0.7rem', height: 'auto', maxWidth: 130,
                                                 '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }} />;
                        return tip ? <Tooltip title={tip}><span>{chip}</span></Tooltip> : chip;
                      })()}
                    </TableCell>
                    <TableCell>
                      {(l.status === 'active' || l.status === 'held') && (
                        <Tooltip title="Stop this lead — they receive nothing further">
                          <span>
                            <IconButton size="small" disabled={stopping === l.id}
                                        aria-label={`Stop ${l.firstName || l.email}`}
                                        onClick={(e) => { e.stopPropagation(); void stop(l); }}
                                        sx={{ color: 'var(--error)' }}>
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {l.status === 'stopped' && l.stoppedBy === VERITY_SYSTEM_ACTOR && (
                        <Tooltip title="Make this stop final — Resume and Undo will no longer apply">
                          <span>
                            <IconButton size="small" disabled={stopping === l.id}
                                        aria-label={`Make the stop of ${l.firstName || l.email} final`}
                                        onClick={(e) => { e.stopPropagation(); setTakeOver(l); }}
                                        sx={{ color: 'var(--error)' }}>
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {/* Hidden once SIMS has emailed the lead: from then on its sequence counts from
                          SIMS's own email, and the server refuses both. */}
                      {canWrite && canMarkWelcomed(l) && (
                        <Tooltip title={l.welcomedOutside
                          ? 'Change the date of the welcome Admissions sent by hand'
                          : 'Mark welcomed outside SIMS — Admissions already sent the first email by hand'}>
                          <IconButton size="small"
                                      aria-label={l.welcomedOutside
                                        ? `Change the welcome date for ${l.firstName || l.email}`
                                        : `Mark ${l.firstName || l.email} welcomed outside SIMS`}
                                      onClick={(e) => { e.stopPropagation(); openWelcome(l, false); }}
                                      sx={{ color: 'var(--text-secondary)' }}>
                            {l.welcomedOutside ? <EditCalendarIcon fontSize="small" /> : <MarkEmailReadIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                      {canWrite && canUndoWelcomed(l) && (
                        <Tooltip title="Undo welcomed outside SIMS — SIMS sends its own welcome first">
                          <IconButton size="small" aria-label={`Undo welcomed outside SIMS for ${l.firstName || l.email}`}
                                      onClick={(e) => { e.stopPropagation(); openWelcome(l, true); }}
                                      sx={{ color: 'var(--text-secondary)' }}>
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Last in the row, away from Stop. It opens a dialog; nothing is sent from here. */}
                      {canWrite && canSendNow(l) && activeEntries.length > 0 && (
                        <Tooltip title="Send email now — send this lead its next email from SIMS">
                          <IconButton size="small" aria-label={`Send ${l.firstName || l.email} its next email now`}
                                      onClick={(e) => { e.stopPropagation(); void openSendNow(l); }}
                                      sx={{ color: 'var(--accent)' }}>
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {!loading && shown.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'var(--text-secondary)' }}>
          Showing {shown.length} of {leads.length}.
        </Typography>
      )}

      {/* The WHOLE record SIMS holds. Fifteen fields will not fit a table anyone can read, so the
          table keeps what is worth scanning and this shows everything - the point being that a
          person never has to open Verity to answer a question about a lead. */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        {detail && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box>
                {[detail.firstName, detail.lastName].filter(Boolean).join(' ') || detail.email}
                <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)' }}>
                  {(detail.source || '').toUpperCase()} ·{' '}
                  {(detail.importedFrom || 'manual').startsWith('eab') ? 'arrived automatically' : 'uploaded by hand'}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              <IconButton onClick={() => setDetail(null)} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {([
                ['Email', detail.email],
                // No phone row, and no postcode in the location: SIMS does not hold either, and
                // showing an empty field implies it should.
                ['Location', [detail.city, detail.state].filter(Boolean).join(', ')],
                ['Programme of interest', detail.programInterest],
                ['Also interested in', detail.additionalInterests],
                ['Degree level', detail.degreeLevel],
                ['Modality', detail.modality],
                ['Start timeframe', detail.startTimeframe],
                ['Work experience', detail.workExperience],
                ['Credentials', detail.credentials],
              ] as const).map(([label, value]) => (
                <Box key={label} sx={{ display: 'flex', py: 0.75, gap: 2 }}>
                  <Typography variant="body2" sx={{ width: 190, color: 'var(--text-secondary)' }}>
                    {label}
                  </Typography>
                  {/* A blank field is shown as blank rather than hidden: "we do not hold this" is
                      itself an answer, and hiding it looks like the field does not exist. */}
                  <Typography variant="body2" sx={{ color: value ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                    {value || 'not provided'}
                  </Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1.5 }} />
              {([
                ['Arrived', arrivalDate(detail.arrivedAt)],
                ['Status', STATUS_STYLE[detail.status]?.label || detail.status],
                ['Emails sent', String((detail.sentSteps || []).length)],
                ['Next', detail.nextStep || detail.nextStepReason || '—'],
                ['Welcomed outside SIMS', welcomedText(detail)],
                ['Last sent', lastSentText(detail)],
                ['Source file', detail.sourceFile],
                ['Lead ID', detail.leadId],
                ['Stopped', detail.stoppedAt
                  ? `${pacificDay(detail.stoppedAt) || detail.stoppedAt} by ${detail.stoppedBy || '—'}${detail.stoppedReason ? ` — ${detail.stoppedReason}` : ''}`
                  : ''],
              ] as const).map(([label, value]) => (
                <Box key={label} sx={{ display: 'flex', py: 0.75, gap: 2 }}>
                  <Typography variant="body2" sx={{ width: 190, color: 'var(--text-secondary)' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: value ? 'var(--text-primary)' : 'var(--text-secondary)',
                                                    wordBreak: 'break-all' }}>
                    {value || '—'}
                  </Typography>
                </Box>
              ))}
            </DialogContent>
            {canWrite && canSendNow(detail) && activeEntries.length > 0 && (
              <DialogActions>
                <Button startIcon={<SendIcon />} onClick={() => { const lead = detail; setDetail(null); void openSendNow(lead); }}>
                  Send email now…
                </Button>
              </DialogActions>
            )}
          </>
        )}
      </Dialog>

      <Dialog open={!!takeOver} onClose={() => setTakeOver(null)}>
        <DialogTitle>Make this stop final?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ overflowWrap: 'anywhere' }}>
            The Verity check stopped {takeOver?.firstName || takeOver?.email}
            {takeOver?.stoppedReason ? ` (${takeOver.stoppedReason})` : ''}. A stop the check made can still be
            resumed or undone. If you stop the lead yourself, the stop becomes yours and is final: Resume and
            Undo will no longer apply.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTakeOver(null)}>Cancel</Button>
          <Button color="error" onClick={() => {
            const lead = takeOver;
            setTakeOver(null);
            if (lead) void stop(lead);
          }}>Make the stop final</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!welcomeMark} onClose={welcomeBusy ? undefined : () => setWelcomeMark(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{welcomeMark?.welcomedOutside ? 'Change the date of the welcome?' : 'Mark welcomed outside SIMS?'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogContentText sx={{ overflowWrap: 'anywhere' }}>
            Admissions already sent {welcomeMark?.firstName || welcomeMark?.email} the first email by hand. SIMS will
            not send its welcome: it starts at the next email, counted from the day of that welcome. Verity is still
            checked before every email.
          </DialogContentText>
          <TextField type="date" size="small" label="Day of the welcome (optional)" value={welcomeDate}
                     onChange={(e) => setWelcomeDate(e.target.value)} disabled={welcomeBusy}
                     InputLabelProps={{ shrink: true }}
                     inputProps={{ min: arrivalDate(welcomeMark?.arrivedAt) || undefined, max: pacificDay(new Date().toISOString()) }}
                     helperText={'Leave blank for the day the lead arrived'
                       + (arrivalDate(welcomeMark?.arrivedAt) ? ` (${arrivalDate(welcomeMark?.arrivedAt)})` : '')
                       + '. Pacific dates; not before the arrival, not after today.'} />
          {welcomeError && <Alert severity="error">{welcomeError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWelcomeMark(null)} disabled={welcomeBusy}>Cancel</Button>
          <Button variant="contained" disabled={welcomeBusy} onClick={() => void saveWelcome()}>
            {welcomeBusy ? 'Saving…' : welcomeMark?.welcomedOutside ? 'Change the date' : 'Mark welcomed outside SIMS'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!welcomeUndo} onClose={welcomeBusy ? undefined : () => setWelcomeUndo(null)}>
        <DialogTitle>Undo welcomed outside SIMS?</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogContentText sx={{ overflowWrap: 'anywhere' }}>
            SIMS will send {welcomeUndo?.firstName || welcomeUndo?.email} its own welcome first, as for any new lead.
            Only undo this if Admissions did not send the first email by hand.
          </DialogContentText>
          {welcomeError && <Alert severity="error">{welcomeError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWelcomeUndo(null)} disabled={welcomeBusy}>Cancel</Button>
          <Button color="warning" disabled={welcomeBusy} onClick={() => void saveWelcome()}>
            {welcomeBusy ? 'Saving…' : 'Undo the mark'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send email now. The person vouches only for what SIMS cannot see in Verity; the server checks
          everything else again when Send is pressed, and Send carries the step this dialog showed. */}
      <Dialog open={!!sendLead} onClose={sendBusy === 'sending' ? undefined : closeSendNow} maxWidth="sm" fullWidth>
        <DialogTitle>Send email now?</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sendBusy === 'checking' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <CircularProgress size={22} />
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>Checking this lead in Verity…</Typography>
            </Box>
          )}
          {sendPreview?.entry && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr)', columnGap: 2, rowGap: 0.75 }}>
              {([
                ['To', [[sendLead?.firstName, sendLead?.lastName].filter(Boolean).join(' '), sendPreview.address]
                  .filter(Boolean).join(' · ')],
                ['Email', `${sendPreview.entry.name || sendPreview.entry.step}`
                  + (sendPreview.entry.day != null ? ` (day ${sendPreview.entry.day})` : '')],
                ['Subject', sendPreview.entry.subject || '—'],
              ] as const).map(([label, value]) => (
                <Box key={label} sx={{ display: 'contents' }}>
                  <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>{label}</Typography>
                  <Typography variant="body2" sx={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          )}
          {/* Said only of a check that was made: a refusal by the kill switch, Disarm or the send window
              comes before Verity is read. */}
          {sendPreview?.entry && (sendPreview.sendable || (sendPreview.entry.statuses || []).length > 0) && (
            <DialogContentText sx={{ overflowWrap: 'anywhere' }}>
              {sendPreview.sendable && (sendPreview.entry.addressConfirmed
                ? 'SIMS checked this lead in Verity just now and found it in order. '
                : 'SIMS checked this lead in Verity just now. Everything is in order except that Verity returned no '
                  + 'email address for it. That is the part you confirm. ')}
              {(sendPreview.entry.statuses || []).length > 0
                && `This email is only for leads whose Verity status is ${orList(sendPreview.entry.statuses)}.`}
            </DialogContentText>
          )}
          {sendPreview && !sendPreview.sendable && sendPreview.why && (
            <Alert severity="warning">{sendPreview.why}</Alert>
          )}
          {/* The welcome is the one email a person may already have sent from Outlook. */}
          {sendPreview?.sendable && sendPreview.entry?.day === 0 && (
            <Alert severity="info">
              This is the welcome. If Admissions already welcomed this lead from Outlook, do not send it: click
              Cancel and mark the lead Welcomed outside SIMS instead.
            </Alert>
          )}
          {sendPreview?.sendable && sendPreview.entry && (
            <>
              <FormControlLabel
                sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
                control={<Checkbox checked={sendAttest} disabled={!!sendBusy}
                                   onChange={(e) => setSendAttest(e.target.checked)} />}
                label={<Typography variant="body2" sx={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
                  I checked this lead in Verity just now. It is still a lead we may email
                  {(sendPreview.entry.statuses || []).length > 0 ? ` (its status is ${orList(sendPreview.entry.statuses)})` : ''},
                  and its email address is {sendPreview.address}, not marked Do Not Email, bad or old.
                  {sendPreview.entry.day === 0 ? ' Nobody has already welcomed this lead from Outlook.' : ''}
                </Typography>} />
              <TextField size="small" label="Reason" value={sendReason} multiline minRows={2}
                         onChange={(e) => setSendReason(e.target.value)} disabled={!!sendBusy}
                         inputProps={{ maxLength: 300 }}
                         helperText="Kept with the send (5-300 characters). For example: Verity does not share email addresses with SIMS yet; checked in Verity by hand." />
            </>
          )}
          {sendError && <Alert severity="error">{sendError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSendNow} disabled={sendBusy === 'sending'}>Cancel</Button>
          <Button variant="contained" startIcon={<SendIcon />} onClick={() => void doSendNow()}
                  disabled={!sendPreview?.sendable || !sendPreview.address || !sendAttest
                            || cleanReason(sendReason).length < 5 || !!sendBusy}>
            {sendBusy === 'sending' ? 'Sending…' : 'Send email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
                message={toast || ''} />
    </Box>
  );
}
